import React, { useEffect, useState } from 'react';
import { Plus, Copy, Send, UserX, UserCheck, Trash2, Users, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { clientsAPI } from '@/lib/clientsAPI';
import { safeFormat } from '@/lib/utils';

const red = 'bg-[#A62B1F] hover:bg-[#8C2318] text-white';
const STATUS = {
  invited: { label: 'Invited', cls: 'bg-sky-50 text-sky-800 border-sky-200' },
  active: { label: 'Active', cls: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
  disabled: { label: 'Access off', cls: 'bg-[#F3EFE7] text-[#5C6670] border-[#E3DDD1]' },
};

export default function Clients() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: '', email: '', phone: '', organization: '', note: '' });
  const [saving, setSaving] = useState(false);

  const load = () => clientsAPI.list().then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const invite = async () => {
    setSaving(true);
    try {
      await clientsAPI.invite({ ...f, phone: f.phone || null, organization: f.organization || null, note: f.note || null });
      toast.success(`${f.name} has been sent their portal invite`);
      setOpen(false);
      setF({ name: '', email: '', phone: '', organization: '', note: '' });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not invite'); } finally { setSaving(false); }
  };

  const act = async (fn, msg) => {
    try { await fn(); if (msg) toast.success(msg); load(); } catch (e) { toast.error(e?.response?.data?.detail || 'Something went wrong'); }
  };

  const shown = (rows || []).filter((c) => !q || `${c.name} ${c.email} ${c.organization || ''}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <Layout title="Clients">
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-[#1E2A33]" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>Clients</h1>
          <p className="text-sm text-[#5C6670]">People you work for. They sign in to their own portal and only see what's shared with them, never your team's work.</p>
        </div>
        <Button className={red} onClick={() => setOpen(true)}><Plus className="w-4 h-4 mr-1" />Invite a client</Button>
      </div>

      <div className="rounded-lg border border-[#E3DDD1] bg-[#FBF8F2] px-4 py-3 mb-4 text-sm text-[#1E2A33]">
        <strong>What a client sees:</strong> open yarns they're in, their interview and press kit, and documents for them to sign. Anything shared with their email address shows up in their portal automatically.
      </div>

      <div className="relative max-w-sm mb-4">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6670]" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search clients" className="pl-9" />
      </div>

      <div className="rounded-xl border border-[#E3DDD1] bg-white overflow-hidden">
        {rows === null ? <p className="p-6 text-sm text-[#5C6670]">Loading…</p> : shown.length === 0 ? (
          <div className="p-10 text-center"><Users className="w-8 h-8 mx-auto text-[#A62B1F] mb-2" /><p className="font-semibold">No clients yet</p>
            <p className="text-sm text-[#5C6670]">Invite a client to give them one place for everything you're doing together.</p></div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#FBF8F2] text-[#5C6670] text-left">
              <tr><th className="px-4 py-2.5 font-medium">Client</th><th className="px-4 py-2.5 font-medium hidden md:table-cell">Organization</th>
                <th className="px-4 py-2.5 font-medium">Access</th><th className="px-4 py-2.5 font-medium hidden md:table-cell">Last sign-in</th><th /></tr>
            </thead>
            <tbody>
              {shown.map((c) => (
                <tr key={c.id} className="border-t border-[#E3DDD1]">
                  <td className="px-4 py-3"><div className="font-semibold text-[#1E2A33]">{c.name}</div><div className="text-xs text-[#5C6670]">{c.email}</div></td>
                  <td className="px-4 py-3 hidden md:table-cell text-[#5C6670]">{c.organization || '—'}</td>
                  <td className="px-4 py-3"><span className={`inline-block rounded-full border px-2 py-0.5 text-xs font-medium ${STATUS[c.status]?.cls}`}>{STATUS[c.status]?.label || c.status}</span></td>
                  <td className="px-4 py-3 hidden md:table-cell text-[#5C6670]">{c.last_login_at ? safeFormat(c.last_login_at, 'MMM d, h:mm a') : 'Not yet'}</td>
                  <td className="px-2 py-3 text-right whitespace-nowrap">
                    {c.status !== 'disabled' && <Button size="sm" variant="ghost" title="Copy their invite link" onClick={async () => { const r = await clientsAPI.inviteLink(c.id); navigator.clipboard.writeText(r.data.link); toast.success('Invite link copied'); }}><Copy className="w-4 h-4" /></Button>}
                    {c.status !== 'disabled' && <Button size="sm" variant="ghost" title="Resend invite" onClick={() => act(() => clientsAPI.resend(c.id), 'Invite sent')}><Send className="w-4 h-4" /></Button>}
                    {c.status === 'disabled'
                      ? <Button size="sm" variant="ghost" title="Turn access back on" onClick={() => act(() => clientsAPI.update(c.id, { disabled: false }), 'Access restored')}><UserCheck className="w-4 h-4" /></Button>
                      : <Button size="sm" variant="ghost" title="Turn off portal access" onClick={() => { if (window.confirm(`Turn off portal access for ${c.name}?`)) act(() => clientsAPI.update(c.id, { disabled: true }), 'Access turned off'); }}><UserX className="w-4 h-4" /></Button>}
                    <Button size="sm" variant="ghost" title="Remove" className="text-[#A62B1F]" onClick={() => { if (window.confirm(`Remove ${c.name} as a client?`)) act(() => clientsAPI.remove(c.id), 'Removed'); }}><Trash2 className="w-4 h-4" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Invite a client</DialogTitle>
            <DialogDescription>They'll get an email to set a password and open their portal. Clients never see your team's internal work.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
              <div><Label>Email</Label><Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
              <div><Label>Mobile (optional)</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></div>
              <div><Label>Organization (optional)</Label><Input value={f.organization} onChange={(e) => setF({ ...f, organization: e.target.value })} /></div>
            </div>
            <div><Label>Personal note (optional)</Label><Textarea rows={3} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="Shown in their invite email" /></div>
            <Button onClick={invite} disabled={saving || !f.name.trim() || !f.email.trim()} className={`w-full ${red}`}>{saving ? 'Sending…' : 'Send invite'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
